import { useQueryClient, useQueries } from '@tanstack/react-query'
import { generatePath } from 'react-router-dom'

import { useDelete, useGetOne, useCreate, usePatch } from '../request'
import { AccountingKeys } from '../Keys'
import type {
  BudgetBreakdownFilters,
  BudgetBreakdownResponse,
  CostCenterBodyRequest,
  CostCenterBodyResponse,
  CostCenterDropdownListResponse,
  CostCenterListResponse,
} from '@models/CostCenters'
import api from '@api/api'

export const PATHS = {
  costCenters: '/api/accounting/cost-center',
  costCentersDropdown: '/api/accounting/cost-center/dropdowns',
  specificCostCenter: '/api/accounting/cost-center/:id',
  specificCostCenterBudget: '/api/accounting/cost-center/:id/budgets/:date',
  constCenterBudgetBreakdown: '/api/accounting/cost-center/:id/budgets/:date/breakdown',
  constCenterBudgetBreakdownFilters: '/api/accounting/cost-center/:id/budgets/:date/breakdown/filters',
}

export const useGetCostCenters = (search?: string, organization?: number, enabled = true) =>
  useGetOne<CostCenterListResponse>([AccountingKeys.CostCenter, organization], PATHS.costCenters, {
    query: {
      enabled,
    },
    axios: {
      params: { search, size: 0, organization },
    },
  })

export const useGetCostCentersDropdownList = (search?: string, enabled = true) =>
  useGetOne<CostCenterDropdownListResponse>([AccountingKeys.CostCenterDropdown], PATHS.costCentersDropdown, {
    query: {
      enabled,
    },
    axios: {
      params: { search, size: 0 },
    },
  })
export const useGetCostCenterDetail = (id: string | undefined, enabled = true, date = '') => {
  return useGetOne<CostCenterBodyResponse>(
    [AccountingKeys.CostCenter, id],
    generatePath(PATHS.specificCostCenter, { id: id || '', date }),
    {
      query: {
        enabled,
        suspense: true,
      },
    },
  )
}

export const useGetCostCenterDetailsByIds = (ids: string[], date = '') => {
  return useQueries({
    queries: ids.map((id) => {
      return {
        queryKey: [AccountingKeys.CostCenter, id, date],
        staleTime: Infinity,
        queryFn: async () =>
          (await api.get<CostCenterBodyResponse>(generatePath(PATHS.specificCostCenterBudget, { id, date }))).data,
      }
    }),
  })
}

export const useGetCostCenterBudgetDetail = (id = '', date = '') => {
  return useGetOne<CostCenterBodyResponse>(
    [AccountingKeys.CostCenter, id, date],
    generatePath(PATHS.specificCostCenterBudget, { id, date }),
    {
      query: {
        enabled: !!id && !!date,
      },
    },
  )
}

export const useGetCostCenterBudgetBreakdown = (id = '', date = '', filters = {}, enabled = true) => {
  return useGetOne<BudgetBreakdownResponse>(
    [AccountingKeys.CostCenter, id, date],
    generatePath(PATHS.constCenterBudgetBreakdown, { id, date }),
    {
      query: {
        enabled: !!id && !!date && enabled,
      },
      axios: {
        params: filters,
      },
    },
  )
}

export const useGetCostCenterBudgetBreakdownFilters = (id = '', date = '') => {
  return useGetOne<BudgetBreakdownFilters>(
    [AccountingKeys.CostCenterBreakdownFilters],
    generatePath(PATHS.constCenterBudgetBreakdownFilters, { id, date }),
    {
      query: {
        enabled: !!id && !!date,
      },
    },
  )
}

export const useCreateCostCenter = () => useCreate<CostCenterBodyRequest>(PATHS.costCenters)

export const useUpdateCostCenter = (id: string | undefined) => {
  const cache = useQueryClient()
  return usePatch<Partial<CostCenterBodyRequest>, CostCenterListResponse>(generatePath(`${PATHS.costCenters}/${id}`), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.CostCenter])
      },
    },
  })
}

export const useDeleteCostCenter = (id: string) => {
  const cache = useQueryClient()
  return useDelete(generatePath(PATHS.specificCostCenter, { id }), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([AccountingKeys.CostCenter])
      },
    },
  })
}
